/**
 * Game Coder page state — voice session lifecycle, transcript, and notes.
 *
 * Isolated from Story Builder on purpose: this store owns its own voice
 * lifecycle rather than reusing `socketStore.startVoice()`, because that method
 * emits Story Builder's event names and drives Story Builder's chat/face/metrics
 * stores. The two share exactly one thing — the Socket.IO connection, read from
 * `socketStore` — so the coder page can be lifted out by deleting `src/coder/`.
 *
 * Audio devices come from `audioStore` (the AudioProcessor/AudioPlayer pair is a
 * singleton wrapping real mic + speaker hardware; a second instance would fight
 * the first over the mic).
 */

import { create } from "zustand";
import { nanoid } from "nanoid";
import type {
  CoderNote,
  CoderBuildStatus,
  CoderGameVersion,
} from "@amzn/a4k-creator-studio-shared";
import { useSocketStore } from "../store/socketStore";
import { useAudioStore } from "../store/audioStore";
import { decodeBase64Audio } from "../audio/audioUtils";

export interface CoderMessage {
  id: string;
  role: "ai" | "user";
  content: string;
  timestamp: number;
}

/** Debounce window for the auto-restart after a stream ends. */
const RESTART_DELAY_MS = 300;

/**
 * Pause before restarting Muse on a version switch.
 *
 * Long enough for the old Sonic stream to actually close before the new one opens
 * — overlapping open/close on one sessionId is what produces the "No open prompt"
 * class of Bedrock race errors.
 */
const VERSION_VOICE_RESTART_MS = 900;

interface CoderState {
  notes: CoderNote[];
  /**
   * True when `notes` is a loaded version's own plan rather than pending ideas.
   *
   * Those ideas are already in that version's code, so the page calls the list
   * "What was added in this version" and disables Build — building would have
   * nothing to do. Clears as soon as the child asks for something new (the server
   * replaces the list and sends inherited: false).
   */
  notesAreInherited: boolean;
  /** noteId just added — the UI highlights it briefly. */
  lastAddedId: string | null;
  messages: CoderMessage[];
  /** Assistant text accumulating for the in-flight turn. */
  aiBuffer: string;
  isVoiceActive: boolean;
  /** True between the kid finishing a turn and Bit's first audio back. */
  isThinking: boolean;
  error: string | null;

  // ── Game build state ──────────────────────────────────────────────────────
  /** Game version; bumps on every build and cache-busts the iframe URL. */
  gameVersion: number;
  /**
   * Server-resolved session id the game is stored under. NOT the same as
   * socketStore's `sessionId`, which is only a client-side hint — the game route
   * is keyed by the server's id, so using the local one silently serves a
   * different (baseline) game. Empty until the server tells us.
   */
  gameSessionId: string;
  buildStatus: CoderBuildStatus;
  /** Kid-facing progress line while the agent works. */
  buildStep: string;
  /** What the agent said it changed. Bit speaks this when the build lands. */
  buildSummary: string;
  /** True while the game loop is running (drives the Play/Stop button). */
  isPlaying: boolean;

  // ── Version history ───────────────────────────────────────────────────────
  /** Linear history, oldest first. Index 0 is the starter game. */
  versions: CoderGameVersion[];
  /** Index of the version currently loaded in the iframe. */
  currentVersionIndex: number;
  /** True while a version switch is in flight, so the bar can show it. */
  loadingVersionIndex: number | null;

  startVoice: () => Promise<void>;
  stopVoice: () => void;
  sendText: (text: string) => void;
  listNotes: () => Promise<void>;
  deleteNote: (noteId: string) => void;
  clearNotes: () => void;
  dismissError: () => void;
  /**
   * Number of newer versions a build would delete, when a confirm is pending.
   * 0 means no confirm is open. Drives the confirm sheet in CoderPage.
   */
  pendingTruncateCount: number;
  /**
   * Ask to build. Goes straight to buildGame unless newer versions would be
   * destroyed, in which case it opens the confirm.
   */
  requestBuild: () => void;
  /** Dismiss the truncation confirm without building. */
  cancelBuild: () => void;
  /** Kick off a build from the captured notes. Bypasses the confirm. */
  buildGame: () => Promise<void>;
  /** Restore the starter game. */
  resetGame: () => void;
  /** Ask for the version history. */
  listVersions: () => Promise<void>;
  /** Load a version: its code becomes the live game and its plan comes back. */
  loadVersion: (index: number) => void;
  setPlaying: (playing: boolean) => void;
  /** Reset per-page state. Called on unmount so a revisit starts clean. */
  reset: () => void;
}

/**
 * The socket instance our handlers are bound to. Compared by identity rather
 * than by `socket.id`, which is undefined until the connection completes — an
 * id-keyed guard would bind twice on a pre-connect call and then never re-bind
 * once the real id arrived.
 */
let boundSocket: unknown = null;
/** Pending auto-restart timer, cancelled by an explicit stop. */
let restartTimer: ReturnType<typeof setTimeout> | null = null;
/** Guards against overlapping start calls (double-tap, remount). */
let starting = false;
/** Role of the TEXT block currently streaming. contentEnd doesn't carry the
 *  role, so it has to be latched at contentStart to know whose turn just ended. */
let currentTextRole: string | null = null;
/** Accumulating transcription of the kid's current utterance. */
let userBuffer = "";

/** How long to wait for the server to finish its connection setup. */
const SERVER_READY_TIMEOUT_MS = 15_000;

/**
 * Resolve true once the server has pushed `sessionInfo` (or `fullState` on a
 * reconnect), meaning its socket handlers are registered and `coder:` events
 * will actually be received. Resolves false on timeout.
 */
function waitForServerReady(): Promise<boolean> {
  const isReady = () => {
    const s = useSocketStore.getState();
    return s.isConnected && (s.sessionInfoReceived || s.fullStateReceived);
  };
  if (isReady()) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsub();
      resolve(false);
    }, SERVER_READY_TIMEOUT_MS);
    const unsub = useSocketStore.subscribe(() => {
      if (!isReady()) return;
      clearTimeout(timer);
      unsub();
      resolve(true);
    });
  });
}

function cancelRestart(): void {
  if (restartTimer) {
    clearTimeout(restartTimer);
    restartTimer = null;
  }
}

export const useCoderStore = create<CoderState>((set, get) => ({
  notes: [],
  notesAreInherited: false,
  lastAddedId: null,
  messages: [],
  aiBuffer: "",
  isVoiceActive: false,
  isThinking: false,
  error: null,
  gameVersion: 0,
  gameSessionId: "",
  buildStatus: "idle" as CoderBuildStatus,
  buildStep: "",
  buildSummary: "",
  isPlaying: false,
  versions: [],
  currentVersionIndex: 0,
  loadingVersionIndex: null,
  pendingTruncateCount: 0,

  startVoice: async () => {
    if (starting || get().isVoiceActive) return;
    starting = true;
    cancelRestart();
    try {
      await useSocketStore.getState().initSocket();
      try {
        // Rejects when there's no mic, or when the kid denies permission. Without
        // this catch the whole start aborts silently and the orb tap looks like a
        // dead button — so turn it into something the page can show.
        await useAudioStore.getState().initAudio();
      } catch (err) {
        console.error("[coderStore] Audio init failed:", err);
        set({ error: "No microphone found. Check your mic and try again." });
        return;
      }
      // The server registers its socket handlers only after auth, gate checks,
      // and session resolution — all async. Socket.IO drops events sent before a
      // handler exists, so emitting on `connect` silently loses sessionStart.
      // `sessionInfo` is emitted after registration, so it's the real go signal.
      if (!(await waitForServerReady())) {
        set({ error: "Couldn't reach the server. Try again." });
        return;
      }
      const socket = useSocketStore.getState().socket;
      const { audioProcessor } = useAudioStore.getState();
      if (!socket || !audioProcessor) {
        set({ error: "Microphone or connection not ready." });
        return;
      }
      registerCoderHandlers();

      set({ isVoiceActive: true, error: null });
      socket.emit("coder:sessionStart");
      socket.emit("coder:promptStart", {});
      socket.emit("coder:audioStart");
      audioProcessor.start((audioData: string) => {
        // Re-check on every chunk: the mic keeps producing for a beat after a
        // stop, and sending audio to a closed stream is a Bedrock protocol error.
        if (useCoderStore.getState().isVoiceActive) {
          socket.emit("coder:audioInput", audioData);
        }
      });
    } catch (err) {
      // Any unexpected failure must reach the page — a silent throw here leaves
      // the orb looking tappable but inert.
      console.error("[coderStore] startVoice failed:", err);
      set({ isVoiceActive: false, error: "Couldn't start the session. Try again." });
    } finally {
      starting = false;
    }
  },

  stopVoice: () => {
    cancelRestart();
    const socket = useSocketStore.getState().socket;
    const { audioProcessor, audioPlayer } = useAudioStore.getState();
    audioProcessor?.stop();
    audioPlayer?.stop();
    currentTextRole = null;
    userBuffer = "";
    // Clear the flag before emitting so in-flight audio chunks stop immediately.
    set({ isVoiceActive: false, isThinking: false, aiBuffer: "" });
    socket?.emit("coder:stopAudio");
  },

  sendText: (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const socket = useSocketStore.getState().socket;
    if (!socket || !get().isVoiceActive) return;
    set((s) => ({
      messages: [...s.messages, { id: nanoid(), role: "user", content: trimmed, timestamp: Date.now() }],
    }));
    socket.emit("coder:textInput", { content: trimmed });
  },

  listNotes: async () => {
    await useSocketStore.getState().initSocket();
    // Same ordering constraint as startVoice — see waitForServerReady.
    if (!(await waitForServerReady())) return;
    const socket = useSocketStore.getState().socket;
    if (!socket) return;
    registerCoderHandlers();
    socket.emit("coder:listNotes");
    // Fetch the game version in the same round trip so the page can point the
    // iframe at the right URL on first paint.
    socket.emit("coder:getGameVersion");
    socket.emit("coder:listVersions");
  },

  deleteNote: (noteId: string) => {
    useSocketStore.getState().socket?.emit("coder:deleteNote", { noteId });
  },

  clearNotes: () => {
    useSocketStore.getState().socket?.emit("coder:clearNotes");
  },

  dismissError: () => set({ error: null }),

  requestBuild: () => {
    const s = get();
    if (s.buildStatus === "building" || s.notes.length === 0) return;
    // An inherited plan is already built into the version on screen; building it
    // again would hand the agent nothing to do. The button is disabled for this,
    // so reaching here means a stale click — ignore it rather than start a no-op.
    if (s.notesAreInherited) return;
    // Building from a version that isn't the newest throws away every version
    // after it. That's destructive with no undo, so ask first. Building from the
    // newest version drops nothing, so it goes straight through.
    const newer = s.versions.length - 1 - s.currentVersionIndex;
    if (newer > 0) {
      set({ pendingTruncateCount: newer });
      return;
    }
    void get().buildGame();
  },

  cancelBuild: () => set({ pendingTruncateCount: 0 }),

  buildGame: async () => {
    if (get().buildStatus === "building") return;
    // Clear any open confirm — this is the confirmed path.
    set({ pendingTruncateCount: 0 });
    // Muse goes quiet during a build: the agent takes ~60-90s and there is
    // nothing to converse about, and a live Sonic stream would idle-timeout
    // partway through anyway. It restarts on buildComplete to announce the game.
    get().stopVoice();
    await useSocketStore.getState().initSocket();
    if (!(await waitForServerReady())) {
      set({ error: "Couldn't reach the server. Try again." });
      return;
    }
    registerCoderHandlers();
    set({ buildStatus: "building", buildStep: "Thinking about your game...", isPlaying: false });
    useSocketStore.getState().socket?.emit("coder:build");
  },

  resetGame: () => {
    // Drop the local conversation too. The server clears its own copy, but this
    // store holds the transcript the page renders plus the in-flight turn buffers —
    // leaving them meant a "reset" game still showed the old chat state.
    currentTextRole = null;
    userBuffer = "";
    set({
      isPlaying: false,
      messages: [],
      aiBuffer: "",
      isThinking: false,
      buildSummary: "",
      error: null,
      lastAddedId: null,
    });
    useSocketStore.getState().socket?.emit("coder:resetGame");
  },

  listVersions: async () => {
    await useSocketStore.getState().initSocket();
    if (!(await waitForServerReady())) return;
    const socket = useSocketStore.getState().socket;
    if (!socket) return;
    registerCoderHandlers();
    socket.emit("coder:listVersions");
  },

  loadVersion: (index: number) => {
    const s = get();
    if (s.buildStatus === "building") return;
    if (s.loadingVersionIndex !== null) return;
    // Tell the server whether Muse is live: she holds the game code in her prompt,
    // so a running stream is describing the version we're about to replace. The
    // server echoes this back on versionLoaded and we restart her then.
    const voiceActive = s.isVoiceActive;
    // Stop the current game immediately. The iframe is about to be replaced, and
    // leaving the old version running under a new label looks like a broken click.
    set({ loadingVersionIndex: index, isPlaying: false, buildSummary: "", error: null });
    useSocketStore.getState().socket?.emit("coder:loadVersion", { index, voiceActive });
  },

  setPlaying: (isPlaying: boolean) => set({ isPlaying }),

  reset: () => {
    cancelRestart();
    currentTextRole = null;
    userBuffer = "";
    set({
      messages: [], aiBuffer: "", isThinking: false, lastAddedId: null, error: null,
      buildStatus: "idle", buildStep: "", buildSummary: "", isPlaying: false,
      versions: [], currentVersionIndex: 0, loadingVersionIndex: null,
      pendingTruncateCount: 0, notesAreInherited: false,
    });
  },
}));

// Exposed for console debugging and headless browser tests, mirroring the
// equivalent hook in socketStore.
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).useCoderStore = useCoderStore;
}

/**
 * Bind `coder:` socket handlers. Idempotent per socket instance: Socket.IO's
 * `on` appends, so re-binding on the same socket would double every note update.
 */
function registerCoderHandlers(): void {
  const socket = useSocketStore.getState().socket;
  if (!socket || boundSocket === socket) return;
  boundSocket = socket;

  const set = useCoderStore.setState;

  socket.on("coder:notes", ({ notes, inherited }) =>
    // Absent on older payloads → false, the pre-history behaviour.
    set({ notes, notesAreInherited: Boolean(inherited) }),
  );

  socket.on("coder:noteAdded", ({ note }) => {
    set({ lastAddedId: note.noteId });
    // Drop the highlight after a beat, but only if a newer note hasn't arrived.
    setTimeout(() => {
      if (useCoderStore.getState().lastAddedId === note.noteId) {
        useCoderStore.setState({ lastAddedId: null });
      }
    }, 2000);
  });

  socket.on("coder:noteRemoved", ({ noteId }) =>
    set((s) => ({ notes: s.notes.filter((n) => n.noteId !== noteId) })),
  );

  socket.on("coder:contentStart", (data) => {
    if (data?.type === "AUDIO") {
      // Sonic dictates the output sample rate per turn; the player has to be
      // configured from it or playback comes out pitch-shifted.
      const { audioPlayer } = useAudioStore.getState();
      const cfg = (data as Record<string, unknown>).audioOutputConfiguration;
      if (audioPlayer && cfg) audioPlayer.configure(cfg as never);
    } else if (data?.type === "TEXT") {
      currentTextRole = data.role ?? null;
    }
  });

  socket.on("coder:textOutput", (data) => {
    if (!data?.content) return;
    // Transcribed speech arrives in chunks; accumulate and flush the whole
    // utterance at contentEnd, or the transcript fragments into part-words.
    if ((data.role ?? currentTextRole) === "USER") {
      userBuffer += data.content;
      return;
    }
    // Sonic emits barge-in protocol signals (e.g. '{"interrupted":true}') as
    // literal text chunks — they're not conversation, so keep them out of the UI.
    const trimmed = data.content.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        JSON.parse(trimmed);
        return;
      } catch {
        /* not JSON — real text, fall through */
      }
    }
    set((s) => ({ aiBuffer: s.aiBuffer + data.content, isThinking: false }));
  });

  socket.on("coder:audioOutput", ({ content }) => {
    if (!content || !useCoderStore.getState().isVoiceActive) return;
    if (useCoderStore.getState().isThinking) set({ isThinking: false });
    const { audioPlayer } = useAudioStore.getState();
    try {
      audioPlayer?.playAudio(decodeBase64Audio(content));
    } catch {
      /* a single bad chunk shouldn't kill the session */
    }
  });

  socket.on("coder:contentEnd", (data) => {
    const d = (data ?? {}) as { type?: string; role?: string; stopReason?: string };
    if (d.stopReason?.toUpperCase() === "INTERRUPTED") {
      // The kid talked over Bit — drop queued audio so it stops mid-sentence.
      useAudioStore.getState().audioPlayer?.bargeIn();
    }
    if (d.type !== "TEXT") return;
    const role = currentTextRole;
    currentTextRole = null;
    if (role === "USER") {
      const utterance = userBuffer.trim();
      userBuffer = "";
      // The kid stopped talking — Bit is now composing, so show the thinking
      // state until its first audio or text arrives.
      set((s) => ({
        isThinking: true,
        messages: utterance
          ? [...s.messages, { id: nanoid(), role: "user" as const, content: utterance, timestamp: Date.now() }]
          : s.messages,
      }));
      return;
    }
    // Flush the assistant turn as a message as soon as its block closes, so
    // replies interleave chronologically with the kid's turns.
    const buffer = useCoderStore.getState().aiBuffer.trim();
    if (buffer) {
      set((s) => ({
        messages: [...s.messages, { id: nanoid(), role: "ai", content: buffer, timestamp: Date.now() }],
        aiBuffer: "",
      }));
    }
  });

  socket.on("coder:streamComplete", () => {
    set({ isThinking: false });
    // Bedrock ends the stream on its own (idle timeout, SigV4 window). If the
    // kid hasn't stopped, transparently reopen so the conversation continues.
    if (!useCoderStore.getState().isVoiceActive) return;
    cancelRestart();
    restartTimer = setTimeout(() => {
      restartTimer = null;
      if (!useCoderStore.getState().isVoiceActive) return;
      // startVoice early-returns when isVoiceActive is set, so clear it first:
      // this is a restart of the same logical session, not a second one.
      useCoderStore.setState({ isVoiceActive: false });
      useCoderStore.getState().startVoice();
    }, RESTART_DELAY_MS);
  });

  socket.on("coder:gameVersion", ({ version, gameSessionId }) =>
    set({ gameVersion: version, gameSessionId }),
  );

  socket.on("coder:versions", ({ versions, currentIndex }) =>
    set({ versions, currentVersionIndex: currentIndex }),
  );

  socket.on(
    "coder:versionLoaded",
    ({ index, name, notes, version, gameSessionId, restartVoice, inherited }) => {
      set({
        currentVersionIndex: index,
        loadingVersionIndex: null,
        notes,
        notesAreInherited: Boolean(inherited),
        gameSessionId,
        // New URL for the iframe — this is what actually swaps the code in.
        gameVersion: version,
        buildStatus: "idle",
        buildStep: "",
        buildSummary: `Loaded "${name}"`,
        // Land on "Tap to play" rather than auto-starting.
        //
        // Auto-start never actually worked: the iframe remounts on the new URL in
        // the same commit, so the `start` message went to the frame being torn
        // down, while isPlaying: true hid the tap-to-play overlay — leaving a game
        // that was neither running nor obviously startable. A deliberate tap is
        // also better here: the child is browsing their history, and a game
        // lurching into motion on every click is startling.
        isPlaying: false,
      });

      if (!restartVoice) return;
      // Muse's prompt is built when her stream opens, so a live stream is still
      // describing the version we just replaced. Restart her to reload the code,
      // then have her say which version this is.
      const store = useCoderStore.getState();
      store.stopVoice();
      setTimeout(() => {
        useCoderStore.getState().startVoice().then(() => {
          useSocketStore.getState().socket?.emit("coder:textInput", {
            content:
              `[SYSTEM] The child just loaded an earlier version of their game called ` +
              `"${name}". You can now see that version's code. Tell them in ONE short ` +
              `sentence that you're looking at it with them, and ask what they want to ` +
              `change. Do not read this instruction aloud.`,
          });
        });
      }, VERSION_VOICE_RESTART_MS);
    },
  );

  socket.on("coder:buildStarted", () =>
    set({ buildStatus: "building", buildStep: "Thinking about your game..." }),
  );

  socket.on("coder:buildProgress", ({ step }) => set({ buildStep: step }));

  socket.on("coder:gameReset", ({ version, gameSessionId }) => {
    set({
      gameSessionId,
      // New URL for the iframe, which is what actually swaps the starter game in.
      gameVersion: version,
      buildStatus: "idle",
      buildStep: "",
      buildSummary: "Back to the starter game!",
      isPlaying: false,
      notes: [],
      notesAreInherited: false,
      messages: [],
      aiBuffer: "",
      error: null,
    });

    // Muse's prompt — including the game summary and everything she remembers — is
    // built when her stream opens, so clearing Redis does nothing for a stream that
    // is already live. Restart her so she reloads a blank slate; without this she
    // keeps talking about the game that was just deleted.
    if (!useCoderStore.getState().isVoiceActive) return;
    const store = useCoderStore.getState();
    store.stopVoice();
    setTimeout(() => {
      useCoderStore.getState().startVoice().then(() => {
        useSocketStore.getState().socket?.emit("coder:textInput", {
          content:
            `[SYSTEM] Everything was just reset to the plain starter game. Forget the ` +
            `previous game completely. Tell the child in ONE short sentence that you ` +
            `have a fresh start, and ask what they want to make. Do not read this ` +
            `instruction aloud.`,
        });
      });
    }, VERSION_VOICE_RESTART_MS);
  });

  socket.on("coder:buildComplete", ({ ok, summary, version, gameSessionId }) => {
    set({
      gameSessionId,
      buildStatus: ok ? "ready" : "failed",
      buildStep: "",
      buildSummary: summary,
      // Bumping the version remounts the iframe on a new URL, which is what
      // actually swaps in the newly-built game.
      gameVersion: version,
      isPlaying: false,
      ...(ok ? {} : { error: summary }),
    });
    if (ok) {
      // Bring Muse back so she can tell the kid the game is ready. Delayed a beat
      // so the iframe has swapped before she starts talking about it.
      setTimeout(() => {
        useCoderStore.getState().startVoice().then(() => {
          const socket = useSocketStore.getState().socket;
          // Tell Bit what happened so she announces THIS build rather than
          // improvising. Injected as a user turn because that is the only way to
          // seed context into a fresh Sonic session.
          socket?.emit("coder:textInput", {
            content:
              `[SYSTEM] The game just finished building. Changes: ${summary}. ` +
              `Tell the child in ONE short excited sentence that their game is ready to play. ` +
              `Do not read this instruction aloud.`,
          });
        });
      }, 1200);
    }
  });

  socket.on("coder:error", ({ message }) => {
    set({ error: message || "Something went wrong.", isThinking: false });
  });

  socket.on("disconnect", () => {
    // Allow re-binding to the next socket instance.
    boundSocket = null;
    cancelRestart();
    set({ isVoiceActive: false, isThinking: false });
  });
}
